// Factory functions and helpers for the Wage Earner ("wageData") request tree.
// Shapes mirror the IuWageData* schemas in seGen/se-api.yml so components/store never
// hand-roll object literals.

export function newEmployment() {
  return {
    employment: {
      employerName: '',
      employeeStatus: null,
      employmentStatus: 'current',
      employmentStartDate: null,
      employmentEndDate: null,
      timeInLineOfWorkMonths: null,
      contractBasisIndicator: null,
      specialRelationshipIndicator: null,
      foreignIncomeIndicator: null,
      virtualCurrencyIndicator: null,
      employmentHistoryMeetsGuidelinesIndicator: null,
      payRates: [],
      employmentHoursSubjectToChangeIndicator: null,
      employmentDependentOnAssignmentIndicator: null,
      employmentHistoryGapIndicator: null,
      seasonalIncomeIndicator: null,
      seasonalIncomeStartMonth: null,
      seasonalIncomeEndMonth: null,
      employmentPosition: null,
      probabilityOfContinuedEmployment: null,
      averageWeeklyHours: null,
      payIncreaseNextDate: null,
      payIncreaseMostRecentDate: null,
      payIncreaseMostRecentAmount: null,
      payIncreaseMostRecentFrequency: null,
      payIncreaseMostRecentFrequencyTimesPerYear: null,
      payIncreaseNextAmount: null,
      payIncreaseNextFrequency: null,
      payIncreaseNextFrequencyTimesPerYear: null,
      employmentLeaves: [],
      incomeDocuments: []
    }
  }
}

export function newPayRate() {
  return {
    category: null,
    militaryCategory: null,
    rateOfPay: null,
    payFrequency: null,
    payFrequencyTimesPerYear: null,
    paymentCycle: null,
    paymentCycleTimesPerYear: null,
    basisType: null,
    incomeLikelyToContinue: null,
    variableIncomeDecreaseExpectedIndicator: null,
    mostRecentPaymentDate: null,
    averageHoursPerCycle: null
  }
}

export function newEmploymentLeave() {
  return {
    employmentLeaveStartDate: null,
    employmentLeaveEndDate: null
  }
}

export function newIncomeDocument() {
  return {
    year: null,
    wageDocumentation: null,
    wageDocumentationSource: 'borrower',
    wageDocumentationDate: null,
    payDate: null,
    payPeriodStartDate: null,
    payPeriodEndDate: null,
    totalWages: null,
    incomeItems: []
  }
}

export function newIncomeItem() {
  return {
    year: null,
    description: '',
    category: null,
    militaryCategory: null,
    hoursCount: null,
    hourlyRateOfPay: null,
    payPeriodAmount: null,
    payTotalAmount: null
  }
}

// Human-readable label for a given employment used by the sidebar,
// e.g. "Employment 1 — Acme, LLC" or "Employment 1" when no employer name yet.
export function getEmploymentLabel(employment, idx) {
  const employerName = employment?.employerName
  return employerName ? `${idx + 1} — ${employerName}` : `Employment ${idx + 1}`
}

// Label helper used by the sidebar to build a readable node name for an income document,
// e.g. "YTD Paystub 04/05/2026", "Year End Paystub 12/18/2025", "W2 2025"
// wageDocumentation is restricted in this UI to: yearToDatePayStub, yearEndPayStub, w2Form
export function getIncomeDocumentLabel(doc) {
  const typeLabel = {
    yearToDatePayStub: 'YTD Paystub',
    yearEndPayStub: 'Year End Paystub',
    w2Form: 'W2'
  }[doc?.wageDocumentation] || 'Document'
  if (doc?.wageDocumentation === 'w2Form') {
    return `${typeLabel} ${doc.year || ''}`.trim()
  }
  return `${typeLabel} ${doc?.payDate || ''}`.trim()
}

// Fields considered "additional information" for an employment. Used to decide whether
// any additional info has been entered for a given employment (e.g. for the Yes/No
// toggle on the Additional Information page, and the Employments summary cards).
export const ADDITIONAL_INFO_FIELDS = [
  'contractBasisIndicator', 'specialRelationshipIndicator', 'foreignIncomeIndicator',
  'virtualCurrencyIndicator', 'employmentHistoryMeetsGuidelinesIndicator',
  'employmentHoursSubjectToChangeIndicator', 'employmentDependentOnAssignmentIndicator',
  'employmentHistoryGapIndicator', 'seasonalIncomeIndicator',
  'timeInLineOfWorkMonths', 'employmentPosition', 'averageWeeklyHours',
  'payIncreaseMostRecentDate', 'payIncreaseMostRecentAmount', 'payIncreaseNextDate', 'payIncreaseNextAmount'
]

export function hasAdditionalEmploymentInfo(employment) {
  return ADDITIONAL_INFO_FIELDS.some(f => {
    let v = employment?.[f]
    return v !== null && v !== undefined && v !== ''
  })
}

// Whether the employment has at least one income document of the given wageDocumentation type
// (one of: yearToDatePayStub, yearEndPayStub, w2Form).
export function hasIncomeDocumentType(employment, wageDocumentation) {
  return (employment?.incomeDocuments || []).some(doc => doc.wageDocumentation === wageDocumentation)
}

